import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { Radio, Users, Lock, Loader2, Clock, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { useUserData } from './UserDataContext';
import { PATHS } from '../router/paths';

interface PreviewMessage {
    senderName: string;
    senderAvatar: string;
    text: string;
    at: number | null;
}

interface RoomPreview {
    exists: boolean;
    ended?: boolean;
    roomId?: string;
    title?: string;
    rules?: string;
    status?: string;
    ageGroup?: 'minor' | 'adult';
    memberCount?: number;
    maxMembers?: number;
    hostName?: string;
    expiresAt?: number | null;
    previewable?: boolean;
    messages?: PreviewMessage[];
}

/** Mirrors ProtectedRoute's leniency so a half-finished signup is not sent in. */
function isProfileComplete(profile: any): boolean {
    if (!profile) return false;
    if (profile.setupComplete === true) return true;
    if (profile.setupComplete === false) return false;
    const hasPhotos = Array.isArray(profile.portfolio) && profile.portfolio.some((p: any) => p && String(p).trim() !== '');
    return !!(profile.name && profile.username && hasPhotos);
}

function timeLeft(expiresAt?: number | null): string {
    if (!expiresAt) return 'Not started yet';
    const ms = expiresAt - Date.now();
    if (ms <= 0) return 'Ending now';
    const mins = Math.round(ms / 60000);
    const hours = Math.floor(mins / 60);
    // Rooms can run up to two days, so a plain hour count reads badly at the top end.
    if (hours >= 24) {
        const d = Math.floor(hours / 24);
        return `${d} day${d === 1 ? '' : 's'} left`;
    }
    if (hours >= 1) return `${hours} hour${hours === 1 ? '' : 's'} left`;
    return `${mins} min left`;
}

/**
 * The public landing page for a room invite: /rooms/:roomId
 *
 * Rooms are invisible to anonymous readers at the data layer — the room document
 * requires a signed-in caller whose age bracket matches, and messages require
 * active membership. So everything here comes from the getPublicRoomPreview
 * callable, which assembles a small sanitised view server-side rather than any
 * rule being loosened.
 *
 * Shaped after TribeInvitePage, and the message wall follows YapTab's public
 * preview: show enough of the real thing to judge it, then ask for an account.
 * A signed-in user never sees this page — they are redirected straight into the
 * room, exactly as the tribe invite page does.
 */
export function RoomInvitePage() {
    const { roomId } = useParams<{ roomId: string }>();
    const navigate = useNavigate();
    const { firebaseUser, userProfile, authLoading } = useUserData();

    const [preview, setPreview] = useState<RoomPreview | null>(null);
    const [loading, setLoading] = useState(true);

    // Already a member of the app: skip the sales pitch, go to the room.
    useEffect(() => {
        if (authLoading) return;
        if (firebaseUser && isProfileComplete(userProfile) && roomId) {
            navigate(`/room/${roomId}`, { replace: true });
        }
    }, [authLoading, firebaseUser, userProfile, roomId, navigate]);

    useEffect(() => {
        if (!roomId) return;
        let cancelled = false;
        (async () => {
            try {
                const { getFunctions, httpsCallable } = await import('firebase/functions');
                const { getApp } = await import('firebase/app');
                const fn = httpsCallable(getFunctions(getApp()), 'getPublicRoomPreview');
                const res = await fn({ roomId });
                if (!cancelled) setPreview((res.data || { exists: false }) as RoomPreview);
            } catch (e) {
                console.error('[RoomInvitePage] preview failed:', e);
                if (!cancelled) setPreview({ exists: false });
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [roomId]);

    const goSignup = () => navigate(PATHS.SIGNUP);
    const goLogin = () => navigate(PATHS.LOGIN, { state: { from: { pathname: `/room/${roomId}` } } });

    // Being redirected into the room — render nothing rather than flashing this page.
    if (!authLoading && firebaseUser && isProfileComplete(userProfile)) return null;

    const shell = (children: React.ReactNode) => (
        <div
            className="min-h-screen flex flex-col items-center"
            style={{
                backgroundColor: '#0d1b2a',
                backgroundImage: `
                    repeating-linear-gradient(45deg, rgba(243,156,18,0.06) 0px, rgba(243,156,18,0.06) 2px, transparent 2px, transparent 22px),
                    repeating-linear-gradient(-45deg, rgba(231,76,60,0.05) 0px, rgba(231,76,60,0.05) 2px, transparent 2px, transparent 22px)
                `,
                backgroundAttachment: 'fixed',
            }}
        >
            {children}
        </div>
    );

    if (authLoading || loading) {
        return (
            <div className="fixed inset-0 flex items-center justify-center" style={{ backgroundColor: '#0d1b2a' }}>
                <Loader2 className="w-8 h-8 animate-spin text-white/40" />
            </div>
        );
    }

    if (!preview?.exists || preview.ended) {
        return shell(
            <div className="flex flex-col items-center justify-center gap-4 text-white p-6 text-center min-h-screen">
                <Radio className="w-12 h-12 text-white/20" />
                <p className="text-lg font-bold">
                    {preview?.ended ? 'This room has ended' : 'Room not found'}
                </p>
                <p className="text-sm text-white/50 max-w-xs">
                    Rooms are temporary and close when their time runs out. Join AfroYap to
                    start one of your own.
                </p>
                <Button onClick={goSignup} className="mt-2 bg-linear-to-r from-[#F39C12] to-[#E74C3C] text-white px-6">
                    Create an account
                </Button>
            </div>,
        );
    }

    const messages = preview.messages || [];
    const isFull = (preview.memberCount || 0) >= (preview.maxMembers || 15);

    return shell(
        <div className="w-full max-w-lg px-5 py-10 flex flex-col gap-6">
            {/* Identity */}
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col items-center text-center gap-3"
            >
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-linear-to-br from-[#F39C12] to-[#E74C3C]">
                    <Radio className="w-8 h-8 text-white" />
                </div>
                <div>
                    <p className="text-xs uppercase tracking-widest text-white/40 font-bold">You're invited to a room</p>
                    <h1 className="text-2xl font-bold text-white mt-1">{preview.title}</h1>
                    <p className="text-sm text-white/50 mt-1">Hosted by {preview.hostName}</p>
                </div>

                <div className="flex items-center gap-4 text-xs text-white/60 mt-1">
                    <span className="flex items-center gap-1.5">
                        <Users className="w-3.5 h-3.5" />
                        {preview.memberCount}/{preview.maxMembers}
                    </span>
                    <span className="flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" />
                        {timeLeft(preview.expiresAt)}
                    </span>
                    {preview.ageGroup === 'adult' && (
                        <span className="px-2 py-0.5 rounded-full bg-white/10 font-bold">18+</span>
                    )}
                </div>
            </motion.div>

            {/* What kind of room this is — the host's own rules, which is the
                clearest statement of the environment anyone gets. */}
            {preview.rules ? (
                <div className="rounded-2xl p-4 bg-white/5 border border-white/10">
                    <p className="text-xs uppercase tracking-wider text-white/40 font-bold mb-2">House rules</p>
                    <p className="text-sm text-white/80 whitespace-pre-wrap">{preview.rules}</p>
                </div>
            ) : null}

            {/* The conversation wall: a real glimpse, then the gate. */}
            <div className="rounded-2xl p-4 bg-white/5 border border-white/10">
                <p className="text-xs uppercase tracking-wider text-white/40 font-bold mb-3 flex items-center gap-1.5">
                    <MessageCircle className="w-3.5 h-3.5" /> In the room right now
                </p>

                {!preview.previewable ? (
                    <p className="text-sm text-white/50">
                        This is an 18+ room, so the conversation stays private until you
                        sign in and we know your age.
                    </p>
                ) : messages.length === 0 ? (
                    <p className="text-sm text-white/50">No one has said anything yet. Be first.</p>
                ) : (
                    <div className="flex flex-col gap-3">
                        {messages.map((m, i) => (
                            <div key={i} className="flex items-start gap-2.5">
                                <div className="w-7 h-7 rounded-full bg-white/10 shrink-0 overflow-hidden">
                                    {m.senderAvatar
                                        ? <img src={m.senderAvatar} alt="" className="w-full h-full object-cover" />
                                        : null}
                                </div>
                                <div className="min-w-0">
                                    <p className="text-xs font-bold text-white/70">{m.senderName}</p>
                                    <p className="text-sm text-white/90 break-words">{m.text}</p>
                                </div>
                            </div>
                        ))}
                        {/* The wall: the preview stops, deliberately and visibly. */}
                        <div className="relative mt-1 pt-4 border-t border-white/10 flex flex-col items-center gap-1">
                            <Lock className="w-4 h-4 text-white/30" />
                            <p className="text-xs text-white/50 text-center">
                                This is the last of what we can show you. Create an account to
                                read the rest and join in.
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2">
                <Button
                    onClick={goSignup}
                    className="w-full h-12 bg-linear-to-r from-[#F39C12] to-[#E74C3C] text-white font-bold rounded-xl"
                >
                    {isFull ? 'Create an account' : 'Create an account to join'}
                </Button>
                <button onClick={goLogin} className="text-sm text-white/50 py-2">
                    Already on AfroYap? <span className="text-white/80 font-bold">Log in</span>
                </button>
                {isFull && (
                    <p className="text-xs text-white/40 text-center">
                        This room is full right now, but rooms open all the time.
                    </p>
                )}
            </div>

            <p className="text-[11px] text-white/30 text-center leading-relaxed">
                Rooms are temporary group conversations on AfroYap. They close when the
                timer runs out and everything in them is deleted.
            </p>
        </div>,
    );
}
